import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAccountUid } from "../contexts/UserContext";
import {
  countPendingApprovals,
  countOutstandingPayments,
  sumOutstandingAmount,
} from "../utils/appointmentQueues";

// Exact counts + outstanding total for the sidebar badge and the Action Queue
// header. Unbounded by date, because a debt does not expire.
//
// Why this exists alongside useQueuePagination: neither queue can be expressed
// as a Firestore filter (see that hook), so `getCountFromServer` cannot produce
// these numbers — the predicates are arithmetic over three fields. A paginated
// list therefore cannot know its own total. This reads the collection once and
// keeps ONLY the handful of fields the predicates touch, so the numbers stay
// exact while the memory cost stays flat no matter how large the docs are.
//
// The page must show a true total: "Total outstanding €1,200" that silently
// means "of what happens to be loaded" is worse than showing nothing.

/** Strips each doc to just what the queue predicates read. */
function projectForCounting(data) {
  return {
    id: data.id,
    status: data.status,
    source: data.source,
    type: data.type,
    date: data.date,
    duration: data.duration,
    price: data.price,
    payment: data.payment
      ? { amountPaid: data.payment.amountPaid, waived: data.payment.waived }
      : undefined,
  };
}

// One shared listener per account, reference-counted.
//
// The sidebar renders on every page and the Action Queue page needs the same
// numbers, so on /action-queue this hook mounts twice. Without sharing, that is
// two identical whole-collection listeners doing the same parsing work. The
// subscriber set also lets late mounts render from the last snapshot instantly
// rather than flashing a zero badge while a fresh listener warms up.
const shared = {
  uid: null,
  unsub: null,
  latest: [],
  subscribers: new Set(),
};

function subscribeToCounts(accountUid, onData) {
  if (shared.uid !== accountUid) {
    if (shared.unsub) shared.unsub();
    shared.uid = accountUid;
    shared.latest = [];
    shared.unsub = onSnapshot(
      query(collection(db, "appointments"), where("groomerId", "==", accountUid)),
      (snap) => {
        shared.latest = snap.docs.map((d) =>
          projectForCounting({ ...d.data(), id: d.id }),
        );
        shared.subscribers.forEach((fn) => fn(shared.latest));
      },
      // A badge is not worth surfacing an error for — fail quiet and show none.
      (err) => {
        console.warn("[useActionQueueCounts] snapshot error:", err?.message);
        shared.latest = [];
        shared.subscribers.forEach((fn) => fn(shared.latest));
      },
    );
  }

  shared.subscribers.add(onData);
  if (shared.latest.length) onData(shared.latest);

  return () => {
    shared.subscribers.delete(onData);
    if (shared.subscribers.size === 0 && shared.unsub) {
      shared.unsub();
      shared.unsub = null;
      shared.uid = null;
      shared.latest = [];
    }
  };
}

export default function useActionQueueCounts() {
  const accountUid = useAccountUid();
  const [projected, setProjected] = useState([]);

  useEffect(() => {
    if (!accountUid) {
      setProjected([]);
      return undefined;
    }
    return subscribeToCounts(accountUid, setProjected);
  }, [accountUid]);

  return useMemo(() => {
    const pending = countPendingApprovals(projected);
    const outstanding = countOutstandingPayments(projected);
    return {
      pending,
      outstanding,
      total: pending + outstanding,
      outstandingTotal: sumOutstandingAmount(projected),
    };
  }, [projected]);
}
